import type { PipelineLostReasonDto } from '@lib/dto/pipeline.dto'
import { LOST_REASON_COUNT_LABELS } from '@lib/enums/lost-reason.enum'
import { lostBeforeAndAfter, lostReasonsInOrder } from '@lib/helpers/pipeline-text'
import type { LeadStatsView } from '@lib/enums/lead-stats-view.enum'
import { Card } from '@/components/ui/card'
import { formatCount } from '@/lib/adFormat'

/**
 * Why couples were lost (docs/pipeline-view.spec.md, "Lost reasons"), most
 * first, each split before and after a tour where tours are recorded: silence
 * before a tour is a question of follow-up, price after one a question of the
 * sale.
 */
/** What the panel says it counts: couples, when it follows couples; losses, when it counts what happened (one couple can be lost twice). */
function counted(total: number, view: LeadStatsView): string {
  if (view === 'cohort') return `${formatCount(total)} ${total === 1 ? 'couple' : 'couples'}, by the reason given`
  return `${formatCount(total)} ${total === 1 ? 'loss' : 'losses'} in these weeks, by the reason given`
}

export function LostPanel({ reasons, view }: { reasons: readonly PipelineLostReasonDto[]; view: LeadStatsView }) {
  const shown = lostReasonsInOrder(reasons)
  const total = shown.reduce((a, r) => a + r.count, 0)
  const split = shown.length > 0 && shown.every((r) => r.afterTour !== null)
  const max = Math.max(1, ...shown.map((r) => r.count))
  const after = shown.reduce((a, r) => a + (r.afterTour ?? 0), 0)
  // Said as the period email says it.
  const summary = lostBeforeAndAfter(reasons)
  return (
    <Card asChild>
      <section id="lost" aria-labelledby="lost-heading" className="flex min-w-0 flex-[1_1_20rem] flex-col gap-4 p-5">
        <div className="space-y-0.5">
          <h2 id="lost-heading" className="m-0 text-heading font-semibold text-ink">
            Why couples were lost
          </h2>
          <p className="m-0 text-small text-ink-muted">{total === 0 ? 'Nobody was lost in these weeks.' : counted(total, view)}</p>
        </div>
        {split ? (
          <div className="flex flex-wrap gap-4 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="inline-block h-3 w-3 rounded-xs bg-line-strong" />
              Before a tour · {formatCount(total - after)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="inline-block h-3 w-3 rounded-xs bg-ink" />
              After a tour · {formatCount(after)}
            </span>
          </div>
        ) : null}
        <ul aria-label="Why couples were lost" className="m-0 flex list-none flex-col gap-3 p-0">
          {shown.map((r) => {
            const afterTour = r.afterTour ?? 0
            return (
              <li key={r.reason} className="space-y-1">
                <p className="m-0 flex justify-between gap-2 text-control">
                  <span className={r.reason === 'not_recorded' ? 'text-ink-muted' : 'text-ink'}>{LOST_REASON_COUNT_LABELS[r.reason]}</span>
                  <span className="font-mono font-semibold tabular-nums text-ink">{formatCount(r.count)}</span>
                </p>
                <div className="flex h-2.5 overflow-hidden rounded-xs bg-surface-sunken" aria-hidden>
                  <div className={split ? 'bg-line-strong' : 'bg-ink-muted'} style={{ width: `${String(((r.count - (split ? afterTour : 0)) / max) * 100)}%` }} />
                  {split ? <div className="bg-ink" style={{ width: `${String((afterTour / max) * 100)}%` }} /> : null}
                </div>
              </li>
            )
          })}
        </ul>
        {summary === null ? null : (
          <div className="mt-auto space-y-1.5 rounded-md bg-surface-sunken px-4 py-3 text-control text-ink">
            <p className="m-0">
              <span className="font-semibold">Before a tour,</span> {summary.before}
            </p>
            <p className="m-0">
              <span className="font-semibold">After a tour,</span> {summary.after}
            </p>
          </div>
        )}
      </section>
    </Card>
  )
}
